/**
 * TrustGate Proof Status Viewer
 * 
 * CRITICAL BOUNDARY: Status derived ONLY from ECS events, never from API responses
 * API responses are informational only. Truth is defined by ECS lifecycle events.
 */

import React, { useEffect, useState } from 'react';
import { CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/init';
import type { LifecycleStatus } from './types';

interface ProofStatusViewerProps {
  requestId: string;
  onStatusChange?: (status: LifecycleStatus) => void;
}

export const ProofStatusViewer: React.FC<ProofStatusViewerProps> = ({ 
  requestId, 
  onStatusChange 
}) => {
  const [status, setStatus] = useState<LifecycleStatus>({
    state: 'unknown',
    requestId,
    derivedFrom: 'ecs_events'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Subscribe to ECS events for this specific request
    // Status is ONLY derived from events, never from createActionRequest response
    const eventsQuery = query(
      collection(db, 'trustgate_events'),
      where('entityId', '==', requestId),
      orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(
      eventsQuery,
      (snapshot) => {
        const events = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        const derivedStatus = deriveStatusFromEvents(events, requestId);
        setStatus(derivedStatus);
        setLoading(false);
        
        if (onStatusChange) {
          onStatusChange(derivedStatus);
        }
      },
      (error) => {
        console.error('❌ Failed to subscribe to events:', error);
        setStatus({
          state: 'unknown',
          requestId,
          derivedFrom: 'ecs_events'
        });
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [requestId, onStatusChange]);

  if (loading) {
    return (
      <div className="flex items-center gap-3 p-4 bg-gray-800/50 border border-gray-700 rounded-lg">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-400" />
        <span className="text-gray-300">Loading status from ECS events...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Current Status Card */}
      <div className={`p-6 rounded-lg border ${getStatusStyles(status.state)}`}>
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 mt-1">
            {getStatusIcon(status.state)}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-1">
              {getStatusTitle(status.state)}
            </h3>
            <p className="text-sm text-gray-300 mb-3">
              {getStatusDescription(status.state)}
            </p>
            
            {/* Request ID */}
            <div className="flex items-center gap-2 text-xs text-gray-400 font-mono">
              <span>Request ID:</span>
              <code className="bg-black/30 px-2 py-1 rounded">{requestId}</code>
            </div>

            {/* Timestamp */}
            {status.timestamp && (
              <div className="flex items-center gap-2 text-xs text-gray-400 mt-2">
                <span>Last Updated:</span>
                <span>{new Date(status.timestamp).toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>

        {/* Status Source Indicator */}
        <div className="mt-4 pt-4 border-t border-gray-700">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <CheckCircle className="w-3 h-3" />
            <span>Status derived from: <strong className="text-cyan-400">ECS Events</strong></span>
          </div>
        </div>
      </div>

      {/* Lifecycle Progress */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
          <span>Lifecycle Progress</span>
        </div>
        <div className="flex gap-2">
          <LifecycleStep 
            label="Requested" 
            active={status.state !== 'unknown'} 
            completed={status.state !== 'unknown'}
          />
          <LifecycleStep 
            label="Approved" 
            active={status.state === 'approved' || status.state === 'executed'} 
            completed={status.state === 'approved' || status.state === 'executed'}
          />
          <LifecycleStep 
            label="Executed" 
            active={status.state === 'executed'} 
            completed={status.state === 'executed'}
          />
        </div>
        
        {/* Denial path */}
        {status.state === 'denied' && (
          <div className="flex gap-2 mt-2">
            <LifecycleStep 
              label="Denied" 
              active={true} 
              completed={true}
              variant="denied"
            />
          </div>
        )}
      </div>

      {/* Proof Data (if available) */}
      {status.proof && (
        <div className="p-4 bg-black/30 border border-gray-700 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span className="text-sm font-semibold text-gray-300">Cryptographic Proof</span>
          </div>
          <pre className="text-xs text-gray-400 overflow-x-auto">
            {JSON.stringify(status.proof, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

/**
 * Lifecycle step indicator component
 */
interface LifecycleStepProps {
  label: string;
  active: boolean;
  completed: boolean;
  variant?: 'normal' | 'denied';
}

const LifecycleStep: React.FC<LifecycleStepProps> = ({ 
  label, 
  active, 
  completed,
  variant = 'normal'
}) => {
  const baseClasses = "flex-1 px-4 py-2 rounded-lg text-xs font-semibold text-center transition-all";
  
  if (variant === 'denied') {
    return (
      <div className={`${baseClasses} bg-red-500/20 border border-red-500/50 text-red-300`}>
        {label}
      </div>
    );
  }
  
  if (completed) {
    return (
      <div className={`${baseClasses} bg-green-500/20 border border-green-500/50 text-green-300`}>
        {label}
      </div>
    );
  }
  
  if (active) {
    return (
      <div className={`${baseClasses} bg-cyan-500/20 border border-cyan-500/50 text-cyan-300 animate-pulse`}>
        {label}
      </div>
    );
  }
  
  return (
    <div className={`${baseClasses} bg-gray-800/50 border border-gray-700 text-gray-500`}>
      {label}
    </div>
  );
};

/**
 * CRITICAL: Derive status from ECS events only
 * API responses are informational, ECS events are truth
 */
function deriveStatusFromEvents(events: any[], requestId: string): LifecycleStatus {
  if (!events || events.length === 0) {
    return {
      state: 'unknown',
      requestId,
      derivedFrom: 'ecs_events'
    };
  }

  // Sort events by timestamp
  const sortedEvents = [...events].sort((a, b) => 
    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  // Find latest significant event
  let latestState: LifecycleStatus['state'] = 'pending';
  let latestTimestamp = '';
  let proof: any = null;

  for (const event of sortedEvents) {
    if (event.type === 'action_request_created') {
      latestState = 'pending';
      latestTimestamp = event.timestamp;
    } else if (event.type === 'action_request_approved') {
      latestState = 'approved';
      latestTimestamp = event.timestamp;
      proof = event.proof || event.receipt;
    } else if (event.type === 'action_request_denied') {
      latestState = 'denied';
      latestTimestamp = event.timestamp;
    } else if (event.type === 'action_executed') {
      latestState = 'executed';
      latestTimestamp = event.timestamp;
      proof = event.proof || event.receipt;
    }
  }

  return {
    state: latestState,
    requestId,
    timestamp: latestTimestamp,
    proof,
    derivedFrom: 'ecs_events'
  };
}

/**
 * Status styling helpers
 */
function getStatusStyles(state: LifecycleStatus['state']): string {
  switch (state) {
    case 'pending':
      return 'bg-yellow-500/10 border-yellow-500/30';
    case 'approved':
      return 'bg-green-500/10 border-green-500/30';
    case 'denied':
      return 'bg-red-500/10 border-red-500/30';
    case 'executed':
      return 'bg-cyan-500/10 border-cyan-500/30';
    default:
      return 'bg-gray-800/50 border-gray-700';
  }
}

function getStatusIcon(state: LifecycleStatus['state']) {
  const iconClass = "w-6 h-6";
  switch (state) {
    case 'pending':
      return <Clock className={`${iconClass} text-yellow-400`} />;
    case 'approved':
      return <CheckCircle className={`${iconClass} text-green-400`} />;
    case 'denied':
      return <XCircle className={`${iconClass} text-red-400`} />;
    case 'executed':
      return <CheckCircle className={`${iconClass} text-cyan-400`} />;
    default:
      return <AlertCircle className={`${iconClass} text-gray-400`} />;
  }
}

function getStatusTitle(state: LifecycleStatus['state']): string {
  switch (state) {
    case 'pending':
      return 'Awaiting Authority';
    case 'approved':
      return 'Approved';
    case 'denied':
      return 'Denied';
    case 'executed':
      return 'Executed';
    default:
      return 'Status Unknown';
  }
}

function getStatusDescription(state: LifecycleStatus['state']): string {
  switch (state) {
    case 'pending':
      return 'Your request has been submitted and is awaiting administrative approval.';
    case 'approved':
      return 'Your request has been approved by an administrator and is ready for execution.';
    case 'denied':
      return 'Your request has been denied by an administrator.';
    case 'executed':
      return 'Your request has been approved and successfully executed.';
    default:
      return 'Unable to determine request status. Please contact support.';
  }
}
